import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { InfoWidget, Button, Tooltip } from '@ds/react-components';
import { Typography, Grid, Divider } from '@material-ui/core';
import moment from 'moment';
import { numberToCurrency } from 'utils/misc';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import * as config from 'common/config.js';

const styles = (theme) => ({
  root: {
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.08)',
    '& h4': {
      fontWeight: 'normal',
      margin: '5px 0',
    },
  },
  error: {
    color: theme.palette.common.error,
  },
  heading: {
    color: '#666666',
    fontSize: '14px',
  },
  icon: {
    color: theme.palette.common.primary,
    marginLeft: theme.spacing(1),
    minWidth: 0,
    fontSize: 16,
  },
  paymentStopped: {
    color: theme.palette.common.yellow,
  },
  noPayments: {
    color: theme.palette.common.success,
  },
  textSmall: {
    color: theme.palette.common.lightText,
    fontSize: '12px',
  },
});

class PaymentSummaryPreview extends React.Component {
  render() {
    const { classes, localisation, customerDetails } = this.props;
    const {
      startDate,
      hasDDStop,
      overdueAmount,
      catchUp,
      catchUpUntil,
      balanceToSettle,
      billNext,
      billNextAmount,
      minSubAmount,
      activePaymethodAccountNo,
      activePaymethodAccountType,
      activeScheduleAmount,
      activeScheduleFrequency,
      activeScheduleAccountNo,
      isFixedTerm,
    } = customerDetails;
    const { dateFormat } = localisation.locales;
    const startDateStr = startDate && moment(startDate).format(dateFormat);
    const nextPaymentDateStr = billNext && moment(billNext).format(dateFormat);
    const totalPayment = billNextAmount || 0;
    const catchUpStr = numberToCurrency(catchUp || 0, localisation);
    const catchUpDateStr = catchUpUntil
      ? `${moment(catchUpUntil).format(dateFormat)}`
      : 'fully paid';
    const tooltip =
      catchUp > 0 ? (
        <Typography variant="body2">
          Including catch up payment of <strong> {catchUpStr} </strong> to be
          debited until <strong>{catchUpDateStr}</strong>
        </Typography>
      ) : (
        ''
      );
    return (
      <InfoWidget
        title=""
        isLoading={
          !customerDetails.adfitNo ||
          balanceToSettle == null ||
          billNextAmount == null ||
          activeScheduleAccountNo == null
        }
        classes={{ root: classes.root }}>
        <Grid container spacing={2} justify="space-between">
          <Grid item>
            {overdueAmount > 0 && (
              <Grid container spacing={2} direction="row">
                <Grid item>
                  <Typography variant="h4">Overdue amount</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="h4" className={classes.error}>
                    {numberToCurrency(overdueAmount || 0, localisation)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item>
            {customerDetails.payNowToken && (
              <Button
                text="Pay Now"
                variant="outlined"
                href={`${config.payNowUri}/${customerDetails.payNowToken}`}
                target="_blank"
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container spacing={4} direction="row" justify="space-between">
          <Grid item xs={12} sm={6} lg={4}>
            <Typography className={classes.heading}>
              Contract start date
            </Typography>
            <Typography variant="h4">{startDateStr}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Typography className={classes.heading}>Contract term</Typography>
            <Typography variant="h4">
              {isFixedTerm ? 'Fixed term' : 'On going'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Grid container alignItems="center">
              <Typography className={classes.heading}>
                Minimum contract value
              </Typography>
              <Tooltip
                text="This amount includes only recurring payments"
                classes={{ arrowPopper: classes.arrowPopper }}
                placement="top">
                <ErrorOutlineIcon className={classes.icon} />
              </Tooltip>
            </Grid>
            <Typography variant="h4">
              {numberToCurrency(minSubAmount || 0, localisation)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Grid container alignItems="center">
              <Typography className={classes.heading}>Next payment </Typography>
              {catchUp > 0 && (
                <Tooltip
                  text={tooltip}
                  classes={{ arrowPopper: classes.arrowPopper }}
                  placement="top">
                  <ErrorOutlineIcon className={classes.icon} />
                </Tooltip>
              )}
            </Grid>
            {!hasDDStop && totalPayment > 0 && (
              <Fragment>
                <Typography variant="h4">
                  {numberToCurrency(totalPayment, localisation)}
                </Typography>
                <Typography variant="body2" className={classes.textSmall}>
                  on {nextPaymentDateStr}
                </Typography>
                <Typography variant="body2" className={classes.textSmall}>
                  {activePaymethodAccountType === 'BA'
                    ? 'from account'
                    : 'on card'}{' '}
                  ending in {activePaymethodAccountNo}
                </Typography>
              </Fragment>
            )}
            {!hasDDStop && totalPayment === 0 && (
              <Typography variant="h4" className={classes.noPayments}>
                Paid fully
              </Typography>
            )}
            {hasDDStop && (
              <Typography variant="h4" className={classes.paymentStopped}>
                Payments stopped
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Typography className={classes.heading}>
              Current plan payment
            </Typography>
            <Typography variant="h4">
              {numberToCurrency(activeScheduleAmount || 0, localisation)}{' '}
              {activeScheduleFrequency &&
                localisation.prompts.frequency &&
                localisation.prompts.frequency[activeScheduleFrequency]}
            </Typography>
            <Typography variant="body2" className={classes.textSmall}>
              to be charged{' '}
              {activePaymethodAccountType === 'BA' ? 'from account' : 'on card'}{' '}
            </Typography>
            <Typography variant="body2" className={classes.textSmall}>
              ending in {activeScheduleAccountNo}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Typography className={classes.heading}>
              Balance to settle
            </Typography>
            <Typography variant="h4">
              {numberToCurrency(balanceToSettle || 0, localisation)}
            </Typography>
          </Grid>
        </Grid>
      </InfoWidget>
    );
  }
}
PaymentSummaryPreview.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PaymentSummaryPreview);
